import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { AppButton } from '@/components'
import { useSavingsStore } from '@/store/useSavingsStore'
import { useUserStore } from '@/store/useUserStore'
import {
  useGetFeaturesSettings,
  useGetWallet,
  type WalletCurrency,
} from '@/domain'
import { useValidateFiatAmount } from '@/hooks/useValidateFiatAmount'
import { currencyUtils } from '@/utils'

const { parseAmountToFloat, cryptoToFiat } = currencyUtils

export const TransferInButtonConfirmation: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const fiatAmount = useSavingsStore((state) => state.goalTransferIn.fiatAmount)
  const cryptoAmount = useSavingsStore(
    (state) => state.goalTransferIn.cryptoAmount,
  )
  const setGoalTransferIn = useSavingsStore((state) => state.setGoalTransferIn)

  const user = useUserStore((s) => s.user)
  const displayCurrency = user?.display_currency || 'USD'

  // Получаем настройки функций и данные кошелька
  const { data: featureSettings, isLoading: isLoadingFeatureSettings } =
    useGetFeaturesSettings()
  const { data: walletData } = useGetWallet()

  // Используем данные из API если доступны, иначе из store
  const completeCurrencies =
    walletData?.completeCurrencies ||
    useUserStore((s) => s.wallet?.completeCurrencies)

  // Находим USDT в кошельке пользователя
  const usdtWallet = completeCurrencies?.find(
    (currency: WalletCurrency) => currency.code === 'USDT',
  )

  // Вычисляем минимальную сумму в отображаемой валюте
  const minConvertedToDisplayCurrency = cryptoToFiat({
    cryptoPricePerUnit: usdtWallet?.pricePerUnit ?? 1,
    amountCrypto: featureSettings?.savings?.min_amount ?? 1,
  })

  // Добавляем небольшой буфер (0.1%) для учета потери точности при конвертации
  const maxAmountWithBuffer = (usdtWallet?.fiatAmount ?? 0) * 1.001

  // Хук валидации суммы
  const { validateFiatAmount } = useValidateFiatAmount({
    cryptoCode: 'USDT',
    min: {
      amount: String(minConvertedToDisplayCurrency),
      code: displayCurrency,
    },
    max: {
      amount: String(maxAmountWithBuffer),
      code: usdtWallet?.fiatCurrency || displayCurrency,
    },
    customMaxMessage: 'api:validationErrors.insufficientBalance',
    onInvalidAmount: () => {
      setGoalTransferIn({
        triggerShake: true,
        fiatAmount: {
          amount: '0',
          code: displayCurrency,
        },
        cryptoAmount: {
          amount: '0',
          code: 'USDT',
        },
      })
    },
  })

  const redirectToConfirmTransfer = () => {
    setTimeout(() => navigate('/savings/transfer-in/confirm-transfer'), 200)
  }

  const handleTransferIn = async () => {
    // Проверяем, что настройки функций загружены
    if (isLoadingFeatureSettings) {
      return
    }

    // Валидация суммы
    const isValid = validateFiatAmount({
      fiatAmount,
    })

    if (!isValid) {
      return
    }

    redirectToConfirmTransfer()
  }

  // Проверяем, можно ли нажать кнопку
  const fiatAmountValue = parseAmountToFloat({
    amount: fiatAmount.amount,
    decimalSeparator: '.',
  })

  const minAmount = minConvertedToDisplayCurrency
  const isDisabled =
    fiatAmountValue < minAmount ||
    isLoadingFeatureSettings ||
    !usdtWallet ||
    fiatAmountValue > maxAmountWithBuffer

  return (
    <AppButton
      label={t('form:actions.transferIn')}
      onPress={handleTransferIn}
      isDisabled={isDisabled}
      isLoading={isLoadingFeatureSettings}
    />
  )
}
